import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, PageQuerySchema, pageSchema } from "./common.js";
import { ProjectSchema } from "./session.js";

export const JWT_ISSUER = "zerovia-auth";
export const JWT_AUDIENCE = "zerovia-platform";

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

/**
 * Platform-wide roles. `platform_admin` is the Zerovia internal operator from
 * design §14 — deliberately NOT the same as Agentry's product `system_admin`.
 */
export const RoleSchema = z.enum([
	"platform_admin",
	"platform_viewer",
	"tenant_admin",
	"tenant_member",
]);
export type Role = z.infer<typeof RoleSchema>;

export const ROLE_RANK: Record<Role, number> = {
	platform_admin: 40,
	platform_viewer: 30,
	tenant_admin: 20,
	tenant_member: 10,
};

/**
 * Passwords are deliberately limited to printable ASCII so visually similar
 * Unicode text cannot accidentally satisfy an operator-facing password field.
 */
export const PasswordSchema = z
	.string()
	.min(8)
	.max(128)
	.regex(/^[\x20-\x7e]+$/, "password must contain printable ASCII characters only")
	.regex(/[A-Z]/, "password must contain an uppercase English letter")
	.regex(/[a-z]/, "password must contain a lowercase English letter")
	.regex(/[0-9]/, "password must contain a digit");
export type Password = z.infer<typeof PasswordSchema>;

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export const TenantSchema = z.object({
	id: IdSchema,
	name: z.string().min(1),
	slug: z.string().min(1),
	status: z.enum(["ACTIVE", "SUSPENDED", "DELETED"]),
	createdAt: IsoDateTimeSchema,
});
export type Tenant = z.infer<typeof TenantSchema>;

export const UserSchema = z.object({
	id: IdSchema,
	email: z.string().email(),
	name: z.string().min(1),
	status: z.enum(["ACTIVE", "DISABLED", "DELETED"]),
	createdAt: IsoDateTimeSchema,
});
export type User = z.infer<typeof UserSchema>;

export const TenantMembershipSchema = z.object({
	id: IdSchema,
	userId: IdSchema,
	tenantId: IdSchema,
	role: RoleSchema,
	status: z.enum(["ACTIVE", "DELETED"]),
	createdAt: IsoDateTimeSchema,
});
export type TenantMembership = z.infer<typeof TenantMembershipSchema>;

// ---------------------------------------------------------------------------
// Requests / responses
// ---------------------------------------------------------------------------

export const SignupRequestSchema = z.object({
	email: z.string().email(),
	password: PasswordSchema,
	name: z.string().min(1).max(100),
	/** Omit to create a new tenant named after the user. */
	tenantName: z.string().min(1).max(100).optional(),
});
export type SignupRequest = z.infer<typeof SignupRequestSchema>;

export const LoginRequestSchema = z.object({
	email: z.string().email(),
	password: z.string().min(1).max(128),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const AuthSessionSchema = z.object({
	accessToken: z.string().min(1),
	expiresAt: IsoDateTimeSchema,
	refreshToken: z.string().min(1),
	refreshExpiresAt: IsoDateTimeSchema,
	user: UserSchema,
	tenant: TenantSchema,
	membership: TenantMembershipSchema,
});
export type AuthSession = z.infer<typeof AuthSessionSchema>;

export const RefreshSessionRequestSchema = z.object({
	refreshToken: z.string().min(1),
});
export type RefreshSessionRequest = z.infer<typeof RefreshSessionRequestSchema>;

/** External signup response: auth session + the default project the user lands in. */
export const SessionBootstrapSchema = AuthSessionSchema.extend({
	project: ProjectSchema,
});
export type SessionBootstrap = z.infer<typeof SessionBootstrapSchema>;

export const MeResponseSchema = z.object({
	user: UserSchema,
	tenant: TenantSchema,
	membership: TenantMembershipSchema,
	permissions: z.array(z.string()),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const CreateUserRequestSchema = z.object({
	email: z.string().email(),
	password: PasswordSchema,
	name: z.string().min(1).max(100),
	role: RoleSchema,
});
export type CreateUserRequest = z.infer<typeof CreateUserRequestSchema>;

export const UpdateUserRequestSchema = z.object({
	email: z.string().email().optional(),
	name: z.string().min(1).max(100).optional(),
});
export type UpdateUserRequest = z.infer<typeof UpdateUserRequestSchema>;

export const ChangePasswordRequestSchema = z.object({
	password: PasswordSchema,
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

export const UpdateTenantRequestSchema = z.object({
	name: z.string().min(1).max(100),
});
export type UpdateTenantRequest = z.infer<typeof UpdateTenantRequestSchema>;

export const SwitchTenantRequestSchema = z.object({
	tenantId: IdSchema,
});
export type SwitchTenantRequest = z.infer<typeof SwitchTenantRequestSchema>;

export const CreateTenantMembershipRequestSchema = z.object({
	tenantId: IdSchema,
	role: RoleSchema,
});
export type CreateTenantMembershipRequest = z.infer<typeof CreateTenantMembershipRequestSchema>;

export const UpdateTenantMembershipRequestSchema = z.object({
	role: RoleSchema,
});
export type UpdateTenantMembershipRequest = z.infer<typeof UpdateTenantMembershipRequestSchema>;

export const LifecyclePageQuerySchema = PageQuerySchema.extend({
	includeDeleted: z
		.enum(["true", "false"])
		.transform((value) => value === "true")
		.default(false),
});
export type LifecyclePageQuery = z.infer<typeof LifecyclePageQuerySchema>;

export const CreateTenantRequestSchema = z.object({
	name: z.string().min(1).max(100),
	slug: z
		.string()
		.min(1)
		.max(60)
		.regex(/^[a-z0-9-]+$/, "slug must be lowercase alphanumeric with hyphens"),
});
export type CreateTenantRequest = z.infer<typeof CreateTenantRequestSchema>;

export const UserPageSchema = pageSchema(UserSchema);
export const TenantPageSchema = pageSchema(TenantSchema);
export const TenantMembershipPageSchema = pageSchema(TenantMembershipSchema);

// ---------------------------------------------------------------------------
// JWT claims — documented for consumers, not a wire schema.
// ---------------------------------------------------------------------------

export const AccessTokenClaimsSchema = z.object({
	sub: IdSchema,
	email: z.string().email(),
	name: z.string(),
	tenantId: IdSchema,
	role: RoleSchema,
});
export type AccessTokenClaims = z.infer<typeof AccessTokenClaimsSchema>;

export const JwksResponseSchema = z.object({
	keys: z.array(z.record(z.string(), z.unknown())),
});
export type JwksResponse = z.infer<typeof JwksResponseSchema>;
